// components/AudioRecorderProvider.tsx
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { createDebugLogger, useLatest } from '@videa/utils';

import { appointmentDataAtom } from '../../atoms/appointment';
import { eventTrackerAtom } from '../../atoms/eventTracker';
import {
  accumulatedDurationAtom,
  enterSilenceAtom,
  exitSilenceAtom,
  isReconstructingBlobAtom,
  isSilentAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  pauseRecordingAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
  recordingBlockedByOtherTabAtom,
  recordingSessionIdAtom,
  selectedLanguageAtom,
  startRecordingAtom,
  stopRecordingAtom,
} from '../../atoms/recording';
import { pendingChunkWritePromiseAtom } from '../../atoms/synapseUpload';
import {
  liveInterimTranscriptAtom,
  liveTranscriptAtom,
} from '../../atoms/transcription';
import { useChunkUpload } from '../../contexts/ChunkUploadContext';
import { buildAudioConstraints } from '../../contexts/microphone';
import { useMicrophoneContext } from '../../contexts/MicrophoneContext';
import { useLiveTranscription } from '../../hooks/useLiveTranscription';
import { useMediaRecorder } from '../../hooks/useMediaRecorder';
import { useSilenceDetection } from '../../hooks/useSilenceDetection';
import {
  acquireRecordingLock,
  destroyRecordingLock,
  initRecordingLock,
  releaseRecordingLock,
} from '../../recording/recordingLock';
import { VideaNotesOPFS, opfs } from '../../storage/opfs';

const debug = createDebugLogger('AudioRecorder');
const opfsDebug = createDebugLogger('OPFS');
const sttDebug = createDebugLogger('STT');
const uploadDebug = createDebugLogger('Upload');

/** Stops all active tracks in a MediaStream */
const stopStreamTracks = (stream: MediaStream | null) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    if (track.readyState !== 'ended') {
      track.stop();
    }
  });
};

/** Chunk interval for OPFS persistence and Synapse upload (30 seconds) */
export const CHUNK_INTERVAL_MS = 30000;

interface AudioRecorderProviderProps {
  children: ReactNode;
  /** Organization ID for OPFS session metadata */
  organizationId?: string;
  /** Practice ID for OPFS session metadata */
  practiceId?: string;
}

export const AudioRecorderProvider = ({
  children,
  organizationId,
  practiceId,
}: AudioRecorderProviderProps) => {
  // Use OPFS when available for persistence and recovery; use Synapse chunk upload when OPFS is used
  const useOPFS = VideaNotesOPFS.isAvailable();
  const useSynapseUpload = useOPFS;
  const { queueChunkUpload } = useChunkUpload();
  // Use useLatest to avoid stale closure - queueChunkUpload may be set after MediaRecorder starts
  const queueChunkUploadRef = useLatest(queueChunkUpload);
  const setLastChunkWrite = useSetAtom(pendingChunkWritePromiseAtom);
  const eventTracker = useAtomValue(eventTrackerAtom);
  const {
    selectedDeviceId,
    selectDevice: setSelectedDevice,
    devices: availableDevices,
  } = useMicrophoneContext();
  const [sessionId, setSessionId] = useAtom(recordingSessionIdAtom);
  const appointmentData = useAtomValue(appointmentDataAtom);
  const sessionIdRef = useRef<string | null>(null);
  const chunkOffsetRef = useRef(0);
  const blobUrlSetFromOPFSRef = useRef(false); // Track if we set blob from OPFS reconstruction
  const isReconstructingRef = useRef(false); // Track if OPFS reconstruction is in progress
  const previousTranscriptsLength = useRef(0); // Track transcript count to avoid duplicates
  const previousInterimTranscript = useRef(''); // Track previous interim transcript
  const isCompletingRef = useRef(false); // Set before stop() to skip SW queue for last chunk

  // Keep sessionIdRef in sync with sessionId atom
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const setStatus = useSetAtom(recorderStatusAtom);
  const setBlobUrl = useSetAtom(mediaBlobUrlAtom);
  const setIsReconstructingBlob = useSetAtom(isReconstructingBlobAtom);
  const setPreviewAudioStream = useSetAtom(previewAudioStreamAtom);
  const handleStartRecordingAtom = useSetAtom(startRecordingAtom);
  const handlePauseRecordingAtom = useSetAtom(pauseRecordingAtom);
  const handleStopRecordingAtom = useSetAtom(stopRecordingAtom);
  const setAccumulatedDuration = useSetAtom(accumulatedDurationAtom);

  const [monitoringStream, setMonitoringStream] = useAtom(monitoringStreamAtom);
  const selectedLanguage = useAtomValue(selectedLanguageAtom);
  const [isCancelPending, setIsCancelPending] = useState(false);

  // Mute detection atoms
  const enterSilence = useSetAtom(enterSilenceAtom);
  const exitSilence = useSetAtom(exitSilenceAtom);
  const setIsSilent = useSetAtom(isSilentAtom);
  const setRecordingBlocked = useSetAtom(recordingBlockedByOtherTabAtom);
  const setRecordingBlockedRef = useLatest(setRecordingBlocked);

  // Live transcription atoms
  const setLiveTranscript = useSetAtom(liveTranscriptAtom);
  const setLiveInterimTranscript = useSetAtom(liveInterimTranscriptAtom);

  // Live transcription for preview
  // Only use local processing to protect customer privacy
  const {
    isSupported: sttSupported,
    isLocalProcessing,
    isListening,
    interimTranscript,
    recentTranscripts,
    startListening,
    stopListening,
    clearTranscripts,
  } = useLiveTranscription({
    lang: selectedLanguage,
    interimResults: true,
    maxLines: 10,
    requireLocalProcessing: true, // Only enable if local processing available
    continuous: true, // Keep listening continuously during recording
  });

  // Clear invalid device selection when device is no longer available
  useEffect(() => {
    if (!selectedDeviceId) return;

    const hasDevice = availableDevices.some(
      (device) => device.deviceId === selectedDeviceId,
    );
    if (!hasDevice) {
      debug.warn(
        `[Microphone] Selected device ${selectedDeviceId} not found, clearing selection`,
      );
      setSelectedDevice(null);
    }
  }, [selectedDeviceId, availableDevices, setSelectedDevice]);

  // Cross-tab recording lock — prevents concurrent recordings
  useEffect(() => {
    initRecordingLock(() => {
      // Remote lock released (other tab stopped, closed, or crashed)
      setRecordingBlockedRef.current?.(false);
    });
    return () => destroyRecordingLock();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, []);

  // Build audio constraints based on selected device.
  const audioConstraints = useMemo(() => {
    if (!selectedDeviceId) {
      return true; // Use default device
    }

    // Check if selected device is still available
    const hasDevice = availableDevices.some(
      (device) => device.deviceId === selectedDeviceId,
    );
    if (!hasDevice) {
      // Device no longer exists - fall back to default
      // (useEffect above will clear the selection)
      return true;
    }

    return buildAudioConstraints(selectedDeviceId);
  }, [selectedDeviceId, availableDevices]);

  // Handle chunk available - save to OPFS for crash recovery
  const handleChunkAvailable = useCallback(
    (chunk: Blob, chunkIndex: number) => {
      const currentSessionId = sessionIdRef.current;
      if (!currentSessionId || !useOPFS) {
        opfsDebug.log(
          `Skipping chunk ${chunkIndex}: sessionId=${currentSessionId}, useOPFS=${useOPFS}`,
        );
        return;
      }

      // Apply offset for resumed recordings
      const actualChunkIndex = chunkIndex + chunkOffsetRef.current;

      eventTracker.track('chunk_upload_started', {
        chunk_index: actualChunkIndex,
      });

      opfsDebug.log(
        `Writing chunk ${actualChunkIndex} (raw: ${chunkIndex}, offset: ${chunkOffsetRef.current}) to session ${currentSessionId}`,
      );

      // Wrap async work in a promise and store it in the atom SYNCHRONOUSLY
      // so completeAmbientSessionAtom can await it before direct-uploading.
      // This fixes the race where a short recording's only ondataavailable fires
      // on recorder.stop() and the OPFS write hasn't finished before the
      // completion flow reads chunks.
      const writePromise = (async () => {
        try {
          await opfs.writeChunk(currentSessionId, actualChunkIndex, chunk);
          opfsDebug.log(`Chunk ${actualChunkIndex} written successfully`);

          // Queue chunk for background upload via service worker (synapse streaming).
          // Skip when completing — the last ondataavailable fires on recorder.stop(),
          // and directUploadFromOPFS will handle it at completion time, avoiding a
          // duplicate upload request.
          if (isCompletingRef.current) {
            uploadDebug.log(
              `Chunk ${actualChunkIndex} written to OPFS (completing — will direct-upload)`,
            );
          } else {
            const currentQueueFn = queueChunkUploadRef.current;
            if (
              useSynapseUpload &&
              currentQueueFn &&
              typeof currentQueueFn === 'function'
            ) {
              try {
                await currentQueueFn(actualChunkIndex, currentSessionId);
                uploadDebug.log(
                  `Chunk ${actualChunkIndex} queued for synapse upload`,
                );
              } catch (uploadError) {
                // Log but don't throw - chunk is in OPFS for recovery
                uploadDebug.error(
                  `Failed to queue chunk ${actualChunkIndex} for synapse upload:`,
                  uploadError,
                );
              }
            }
          }
        } catch (error) {
          // TODO: Send to Sentry
          opfsDebug.error(
            `Chunk ${actualChunkIndex} write failed for session ${currentSessionId}:`,
            error,
          );
        }
      })();

      // Store synchronously — before the microtask queue runs any awaits
      setLastChunkWrite(writePromise);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
    [eventTracker, useSynapseUpload, useOPFS, setLastChunkWrite],
  );

  // Handle recording complete - reconstruct full blob from OPFS if we resumed
  const handleRecordingComplete = useCallback(
    async (blob: Blob) => {
      const currentSessionId = sessionIdRef.current;
      const wasResumed = chunkOffsetRef.current > 0;

      opfsDebug.log(
        `Recording complete: sessionId=${currentSessionId}, wasResumed=${wasResumed}, chunkOffset=${chunkOffsetRef.current}, blobSize=${blob.size}`,
      );

      // IMPORTANT: Set this flag BEFORE any await to prevent the sync effect
      // from setting the partial blob while we reconstruct the full blob.
      // The atom is also set so AudioRecorder can distinguish reconstruction-
      // in-progress from a dead-stream (stopped with no audio data).
      if (wasResumed && currentSessionId && useOPFS) {
        isReconstructingRef.current = true;
        setIsReconstructingBlob(true);
      }

      if (currentSessionId && useOPFS) {
        try {
          opfsDebug.log(
            `Updating session ${currentSessionId} status to pending-upload`,
          );
          await opfs.updateSessionStatus(currentSessionId, 'pending-upload');
        } catch (error) {
          // TODO: Send to Sentry
          opfsDebug.error(
            `Status update failed for session ${currentSessionId}:`,
            error,
          );
        }

        // If we resumed a session, we need to reconstruct the full blob from OPFS
        // because the MediaRecorder only has the new chunks
        if (wasResumed) {
          try {
            opfsDebug.log(
              `Reconstructing blob for resumed session ${currentSessionId}`,
            );
            const fullBlob = await opfs.reconstructBlob(currentSessionId);
            opfsDebug.log(`Blob reconstructed: ${fullBlob.size} bytes`);

            // Only clean up OPFS when NOT using Synapse flow.
            // Synapse flow needs the OPFS metadata (standalonePatientVisitId, etc.)
            // in finishRecording's complete-upload call. That flow handles its own
            // OPFS cleanup after the nucleus API call succeeds.
            if (!useSynapseUpload) {
              opfs.deleteSession(currentSessionId).catch((error) => {
                // Non-critical: session will be cleaned up by stale session cleanup
                opfsDebug.log(
                  `Session cleanup after reconstruction failed:`,
                  error,
                );
              });
            }

            const fullBlobUrl = URL.createObjectURL(fullBlob);
            // Override the mediaBlobUrl with the complete blob
            // Set flag so the sync effect doesn't overwrite it
            blobUrlSetFromOPFSRef.current = true;
            isReconstructingRef.current = false;
            setIsReconstructingBlob(false);
            setBlobUrl(fullBlobUrl);
            return; // Success - exit early
          } catch (error) {
            opfsDebug.error(
              `Blob reconstruction failed for session ${currentSessionId}:`,
              error,
            );
            // Fall back to MediaRecorder blob - lose old chunks but keep new ones
            isReconstructingRef.current = false;
            setIsReconstructingBlob(false);
          }
        }
      } else {
        opfsDebug.log(
          `Skipping OPFS handling: sessionId=${currentSessionId}, useOPFS=${useOPFS}`,
        );
      }

      // Default: use the blob from MediaRecorder (works for non-resumed sessions
      // and as fallback when OPFS reconstruction fails)
      // Don't set the flag - let the effect handle it normally
    },
    [setBlobUrl, setIsReconstructingBlob, useOPFS, useSynapseUpload],
  );

  // Handle recording error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleError = useCallback((_error: Error) => {
    // Recording error - MediaRecorder will handle cleanup
  }, []);

  // Use our custom media recorder with chunk streaming
  const {
    status,
    mediaBlobUrl,
    previewStream,
    startRecording: startMediaRecorder,
    stopRecording: stopMediaRecorder,
    pauseRecording: pauseMediaRecorder,
    resumeRecording: resumeMediaRecorder,
    clearBlobUrl,
  } = useMediaRecorder({
    audio: audioConstraints,
    timeslice: CHUNK_INTERVAL_MS,
    onChunkAvailable: handleChunkAvailable,
    onRecordingComplete: handleRecordingComplete,
    onError: handleError,
  });

  // Create OPFS session before starting recording (or resume existing session)
  // Pass resumeSessionId directly when resuming from recovery to avoid race conditions
  const startRecording = useCallback(
    async (resumeSessionId?: string) => {
      // Cross-tab recording lock — block if another tab is recording
      setRecordingBlocked(false);
      if (!acquireRecordingLock()) {
        setRecordingBlocked(true);
        eventTracker.track('recording_blocked_by_other_tab');
        return;
      }

      opfsDebug.log(
        `startRecording called: resumeSessionId=${resumeSessionId}, atomSessionId=${sessionId}`,
      );

      // Clear previous transcripts and reset tracking refs
      clearTranscripts();
      previousTranscriptsLength.current = 0;
      previousInterimTranscript.current = '';
      isCompletingRef.current = false;

      // Use passed sessionId (from recovery) or fall back to atom value
      const targetSessionId = resumeSessionId || sessionId;

      if (useOPFS) {
        // If resumeSessionId is explicitly passed, we MUST use it (don't create new)
        if (resumeSessionId) {
          // Explicitly resuming from recovery - use the session ID directly
          opfsDebug.log(
            `Resuming from recovery with session ${resumeSessionId}`,
          );
          sessionIdRef.current = resumeSessionId;
          setSessionId(resumeSessionId);

          // Try to read existing chunk count, default to 0 if fails
          try {
            const { metadata } = await opfs.readSession(resumeSessionId);
            chunkOffsetRef.current = metadata.chunkCount;
            // Seed the timer with approximate elapsed time from existing chunks
            const elapsedSeconds =
              metadata.chunkCount * (CHUNK_INTERVAL_MS / 1000);
            setAccumulatedDuration(elapsedSeconds);
            opfsDebug.log(
              `Resumed session has ${metadata.chunkCount} existing chunks (~${elapsedSeconds}s)`,
            );
          } catch (error) {
            // TODO: Send to Sentry
            opfsDebug.error(
              `Session read failed on resume for ${resumeSessionId}:`,
              error,
            );
            // Still use the session ID - new chunks will start from 0
            chunkOffsetRef.current = 0;
          }
        } else if (targetSessionId) {
          // Session ID from atom (not explicit resume) - verify it exists
          opfsDebug.log(`Checking if atom session ${targetSessionId} exists`);
          const exists = await opfs.sessionExists(targetSessionId);
          if (exists) {
            try {
              const { metadata } = await opfs.readSession(targetSessionId);
              chunkOffsetRef.current = metadata.chunkCount;
              sessionIdRef.current = targetSessionId;
              opfsDebug.log(
                `Using existing session ${targetSessionId} with ${metadata.chunkCount} chunks`,
              );
            } catch (error) {
              // TODO: Send to Sentry
              opfsDebug.error(
                `Session read failed for ${targetSessionId}:`,
                error,
              );
              setSessionId(null);
              await createNewSession();
            }
          } else {
            opfsDebug.log(`Session ${targetSessionId} not found, creating new`);
            setSessionId(null);
            await createNewSession();
          }
        } else {
          opfsDebug.log('No existing session, creating new');
          await createNewSession();
        }
      }

      // Then start recording
      await startMediaRecorder();

      async function createNewSession() {
        try {
          const newSessionId = await opfs.createSession({
            // Use unified ID as the appointment ID (it's a string representation)
            unifiedAppointmentId: appointmentData?.unifiedId,
            standalonePatientVisitId: appointmentData?.standalonePatientVisitId,
            patientVideaId: appointmentData?.patient?.videaId,
            organizationId,
            practiceId,
            // Enhanced appointment metadata
            appointmentStart: appointmentData?.appointmentStartsAt,
            estimatedVisitId: appointmentData?.estimatedVisitId,
            imagingPatientId: appointmentData?.patient?.imagingPatientId,
            pmsPatientId: appointmentData?.patient?.pmsPatientId,
            // External patient ID (ChartNum) for recovery
            externalPatientId: appointmentData?.patient?.sourcePatientId,
            // Patient name for recovery (needed for /ambient standalone notes)
            patientFirstName: appointmentData?.patient?.firstName,
            patientLastName: appointmentData?.patient?.lastName,
          });
          sessionIdRef.current = newSessionId;
          setSessionId(newSessionId);
          chunkOffsetRef.current = 0;
          blobUrlSetFromOPFSRef.current = false;
          opfsDebug.log(`Created new session ${newSessionId}`);
        } catch (error) {
          // TODO: Send to Sentry
          opfsDebug.error('Session creation failed:', error);
          // Recording will continue without persistence
        }
      }
    },
    [
      sessionId,
      appointmentData,
      organizationId,
      practiceId,
      setSessionId,
      startMediaRecorder,
      clearTranscripts,
      useOPFS,
      setAccumulatedDuration,
      eventTracker,
      setRecordingBlocked,
    ],
  );

  // Wrap stop to handle session cleanup.
  // Set isCompletingRef BEFORE stop() so the synchronous ondataavailable
  // callback for the last chunk sees it and skips the SW queue.
  const stopRecording = useCallback(() => {
    releaseRecordingLock();
    isCompletingRef.current = true;
    stopMediaRecorder();
  }, [stopMediaRecorder]);

  // Sync recorder status to atoms
  useEffect(() => {
    setStatus(status);
    if (status === 'recording') {
      handleStartRecordingAtom();
    } else if (status === 'paused') {
      handlePauseRecordingAtom();
    } else if (status === 'stopped') {
      handleStopRecordingAtom();
    } else if (status === 'recorder_error' || status === 'permission_denied') {
      // Release cross-tab lock if recording failed to start (e.g. getUserMedia
      // denied, device unavailable). Without this, the heartbeat keeps
      // broadcasting and blocks other tabs indefinitely.
      releaseRecordingLock();
    }
  }, [
    status,
    setStatus,
    handleStartRecordingAtom,
    handlePauseRecordingAtom,
    handleStopRecordingAtom,
  ]);

  // Pre-recording silence detection — runs on the monitoring stream while
  // the sheet is open but recording hasn't started yet.  Uses a short 1.5 s
  // delay so the banner doesn't flash during the brief moment between
  // stream creation and audio data arriving, while still giving fast
  // feedback that the mic isn't working.
  //
  // Writes isSilentAtom directly (not enterSilenceAtom/exitSilenceAtom)
  // because those atoms guard on isRecordingAtom and also manage mute
  // segment metadata that only makes sense during an active recording.
  const preRecordingSilenceStart = useCallback(
    () => setIsSilent(true),
    [setIsSilent],
  );
  const preRecordingSilenceEnd = useCallback(
    () => setIsSilent(false),
    [setIsSilent],
  );

  useSilenceDetection({
    stream: monitoringStream,
    enabled: status === 'idle' && monitoringStream !== null,
    silenceDelayMs: 1_500,
    onSilenceStart: preRecordingSilenceStart,
    onSilenceEnd: preRecordingSilenceEnd,
  });

  // In-recording silence detection — uses the recording stream with a 10 s
  // delay to avoid false positives during natural conversational pauses
  // (examining a patient, reading a chart, etc.).
  useSilenceDetection({
    stream: previewStream,
    enabled: status === 'recording',
    silenceDelayMs: 10_000,
    onSilenceStart: enterSilence,
    onSilenceEnd: exitSilence,
  });

  // Sync mediaBlobUrl to atom, with special handling for cancel flow and OPFS reconstruction
  useEffect(() => {
    if (isCancelPending && mediaBlobUrl) {
      clearBlobUrl();
      setBlobUrl(undefined);
      setIsCancelPending(false);
      blobUrlSetFromOPFSRef.current = false;
      isReconstructingRef.current = false;
      setIsReconstructingBlob(false);
      // Clean up OPFS session on cancel
      const currentSessionId = sessionIdRef.current;
      if (currentSessionId) {
        opfs.deleteSession(currentSessionId).catch((error) => {
          // TODO: Send to Sentry
          opfsDebug.error('Session delete failed on cancel:', error);
        });
        sessionIdRef.current = null;
        setSessionId(null);
      }
    } else if (isCancelPending && !mediaBlobUrl) {
      // Dead-stream case: cancel was requested but no blob exists or will be
      // produced (e.g., recording stopped with no audio data). Reset the
      // recorder to idle and clear the cancel flag immediately — otherwise
      // isCancelPending stays stuck and destroys the next recording's blob.
      // This branch is safe for normal cancel/generate flows because those
      // only call clear() after mediaBlobUrl is already defined.
      clearBlobUrl(); // Resets useMediaRecorder status to 'idle'
      setBlobUrl(undefined);
      setIsCancelPending(false);
      setIsReconstructingBlob(false);
    } else if (isReconstructingRef.current) {
      // Reconstruction in progress - don't set partial blob, wait for full blob
      opfsDebug.log('Skipping partial blob sync - reconstruction in progress');
    } else if (blobUrlSetFromOPFSRef.current) {
      // Don't overwrite - we already set the full blob from OPFS reconstruction
      // Reset the flag for next recording
      blobUrlSetFromOPFSRef.current = false;
    } else {
      setBlobUrl(mediaBlobUrl);
    }
  }, [
    mediaBlobUrl,
    isCancelPending,
    clearBlobUrl,
    setBlobUrl,
    setSessionId,
    setIsReconstructingBlob,
  ]);

  // Sync previewStream to atom
  useEffect(() => {
    if (previewStream?.active) {
      setPreviewAudioStream(previewStream);
    } else {
      setPreviewAudioStream(null);
    }
  }, [previewStream, setPreviewAudioStream]);

  // Log local STT support on mount
  useEffect(() => {
    if (sttSupported) {
      sttDebug.log('Speech recognition supported with local processing');
      if (isLocalProcessing) {
        sttDebug.log('🔒 Local processing enabled - customer audio protected');
      } else {
        sttDebug.log(
          '⚠️ Local processing not available - STT disabled for privacy',
        );
      }
    } else {
      sttDebug.log(
        'Speech recognition not supported or local processing unavailable',
      );
    }
  }, [sttSupported, isLocalProcessing]);

  // Sync transcripts to atoms and persist to OPFS
  useEffect(() => {
    setLiveTranscript(recentTranscripts);
    setLiveInterimTranscript(interimTranscript);

    // Persist transcript context to OPFS for recovery
    if (recentTranscripts.length > 0) {
      const context = recentTranscripts.slice(-3).join(' ');
      const currentSessionId = sessionIdRef.current;
      if (currentSessionId && useOPFS) {
        opfs
          .updateTranscriptContext(currentSessionId, context)
          .catch((error) => {
            // TODO: Send to Sentry
            opfsDebug.error('Transcript context update failed:', error);
          });
      }
    }

    // Persist full transcripts to OPFS for enhanced recovery
    const currentSessionId = sessionIdRef.current;

    // Log why we might not save transcripts
    if (!currentSessionId) {
      opfsDebug.log('[Transcript] No session ID - skipping transcript save');
      return;
    }

    if (!useOPFS) {
      opfsDebug.warn('[Transcript] OPFS not available in this browser');
      return;
    }

    if (currentSessionId && useOPFS) {
      opfsDebug.log(
        `[Transcript] Session: ${currentSessionId}, recentTranscripts: ${recentTranscripts.length} items`,
        recentTranscripts.length > 0 ? recentTranscripts : '(empty)',
        'interimTranscript:',
        interimTranscript || '(empty)',
      );

      // Only persist new final transcripts (avoid duplicates by checking length difference)
      const previousLength = previousTranscriptsLength.current || 0;
      if (recentTranscripts.length > previousLength) {
        // Only append the new transcripts
        const newTranscripts = recentTranscripts.slice(previousLength);
        opfsDebug.log(
          `[Transcript] Appending ${newTranscripts.length} new transcripts:`,
          newTranscripts,
        );
        newTranscripts.forEach((transcript) => {
          if (transcript.trim()) {
            opfs
              .appendTranscript(currentSessionId, transcript, {
                interim: false,
                chunkIndex: Math.floor(Date.now() / CHUNK_INTERVAL_MS),
              })
              .then(() => {
                opfsDebug.log(
                  `[Transcript] Successfully appended final transcript: "${transcript}"`,
                );
              })
              .catch((error) => {
                opfsDebug.error('Transcript append failed:', error);
              });
          }
        });
        previousTranscriptsLength.current = recentTranscripts.length;
      }

      // Persist interim transcript (always update as it changes)
      if (
        interimTranscript.trim() &&
        interimTranscript !== previousInterimTranscript.current
      ) {
        opfsDebug.log(
          `[Transcript] Appending interim transcript: "${interimTranscript}"`,
        );
        opfs
          .appendTranscript(currentSessionId, interimTranscript, {
            interim: true,
            chunkIndex: Math.floor(Date.now() / CHUNK_INTERVAL_MS),
          })
          .then(() => {
            opfsDebug.log(
              `[Transcript] Successfully appended interim transcript: "${interimTranscript}"`,
            );
          })
          .catch((error) => {
            opfsDebug.error('Interim transcript append failed:', error);
          });
        previousInterimTranscript.current = interimTranscript;
      }
    } else {
      opfsDebug.log(
        `[Transcript] Skipping - sessionId: ${currentSessionId}, useOPFS: ${useOPFS}`,
      );
    }
  }, [
    recentTranscripts,
    interimTranscript,
    setLiveTranscript,
    setLiveInterimTranscript,
    useOPFS,
  ]);

  // Sync STT with recording state
  useEffect(() => {
    sttDebug.log(
      `Sync check - supported: ${sttSupported}, status: ${status}, isListening: ${isListening}`,
    );

    if (!sttSupported) {
      sttDebug.warn('Speech recognition not supported in this browser');
      return;
    }

    if (status === 'recording' && !isListening) {
      sttDebug.log('Starting speech recognition...');
      startListening();
    } else if (status !== 'recording' && status !== 'paused' && isListening) {
      sttDebug.log('Stopping speech recognition...');
      stopListening();
    }
  }, [status, sttSupported, isListening, startListening, stopListening]);

  // Clear function for explicit cleanup
  // deleteOpfsSession: true for cancel flow, false for success flow (where
  // handleRecordingComplete may still need to reconstruct blob from OPFS)
  const clear = useCallback(
    (deleteOpfsSession = true) => {
      releaseRecordingLock();
      setIsCancelPending(true);
      stopMediaRecorder();
      stopListening();
      clearTranscripts();
      stopStreamTracks(previewStream);
      stopStreamTracks(monitoringStream);
      setPreviewAudioStream(null);
      setMonitoringStream(null);

      // Only delete OPFS session if explicitly requested (cancel flow)
      // For success flow, handleRecordingComplete handles cleanup after blob reconstruction
      const currentSessionId = sessionIdRef.current;
      if (currentSessionId && deleteOpfsSession) {
        opfs.deleteSession(currentSessionId).catch((error) => {
          // TODO: Send to Sentry
          opfsDebug.error('Session delete failed on clear:', error);
        });
      }

      // Always reset refs and state
      sessionIdRef.current = null;
      setSessionId(null);
      chunkOffsetRef.current = 0;
      blobUrlSetFromOPFSRef.current = false;
      previousTranscriptsLength.current = 0;
      previousInterimTranscript.current = '';
    },
    [
      stopMediaRecorder,
      stopListening,
      clearTranscripts,
      previewStream,
      monitoringStream,
      setPreviewAudioStream,
      setMonitoringStream,
      setSessionId,
    ],
  );

  // Expose control handlers via window for external access
  useEffect(() => {
    (window as any).__audioRecorder = {
      start: startRecording,
      stop: stopRecording,
      pause: pauseMediaRecorder,
      resume: resumeMediaRecorder,
      clear,
    };
  }, [
    startRecording,
    stopRecording,
    pauseMediaRecorder,
    resumeMediaRecorder,
    clear,
  ]);

  return <>{children}</>;
};
